import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  ControlUnit,
  Divider,
} from '@features/ui';
import { cn, formatCurrency, formatDateByMST } from '@lib/utils.ts';
import { LeelooAccounts } from '@src/types/users';

interface CardProps {
  title: string;
  className?: string;
  children: ReactNode;
}
const Card: FC<CardProps> = ({ title, children, className }) => {
  return (
    <div className={cn('flex flex-col gap-y-2', className)}>
      <p className={'text-sm font-bold'}>{title}</p>
      {children}
    </div>
  );
};

interface ChartInformationProps {
  accounts?: LeelooAccounts[];
}

export const ChartInformation: FC<ChartInformationProps> = ({ accounts }) => {
  const { t } = useTranslation();

  // Use first account if available, otherwise use defaults
  const account = accounts?.[0];
  const accountType = account?.account_type || 'rithmic';
  const accountName = account?.rithmic_account_name;

  return (
    <div>
      <div className={'flex px-5 justify-between gap-x-5 mb-3 text-xs'}>
        <Card title={t('dashboard.chartHeader.accountNo')}>
          <div className={'flex items-center gap-x-1'}>
            <Avatar className="w-[21px] h-[21px]">
              <AvatarImage alt="Profile picture" src={''} />
              <AvatarFallback
                className={
                  accountType === 'rithmic' ? 'bg-[#C33838]' : 'bg-[#3490FF]'
                }
              >
                {accountType === 'rithmic' ? 'R' : 'T'}
              </AvatarFallback>
            </Avatar>
            <p>{accountName || 'N/A'}</p>
          </div>
        </Card>
        <Card title={t('dashboard.chartHeader.packages')}>
          <p>{account?.product?.title || 'N/A'}</p>
        </Card>
        <Card title={t('dashboard.chartHeader.invoice')}>
          <p className={'text-[#3490FF]'}>
            {account?.id ? `#${account.id}` : 'N/A'}
          </p>
        </Card>
        <Card
          className={'max-w-40'}
          title={t('dashboard.chartHeader.validDates')}
        >
          <p>
            {account?.valid_from && account?.valid_to
              ? `${formatDateByMST(account.valid_from)} to ${formatDateByMST(account.valid_to)}`
              : 'N/A'}
            {account?.created_at &&
              ` Created ${formatDateByMST(account.created_at)}`}
          </p>
        </Card>
        <Card
          className={'min-w-40'}
          title={t('dashboard.chartHeader.tradingDaysSwing')}
        >
          <div className={'flex flex-col gap-y-[6px]'}>
            <div className={'flex gap-x-1'}>
              <ControlUnit value={account?.trading_days?.toString() || '0'} />
              <ControlUnit
                active
                value={account?.extra_trading_days?.toString() || '0'}
              />
            </div>
            <ControlUnit isFullWidth value={'0%'} />
          </div>
        </Card>
        <Card title={t('dashboard.chartHeader.balanceCurrency')}>
          <p>
            ${formatCurrency(Number(account?.account_balance ?? 0), 2)}
            {account?.account_balance_date &&
              ` ${formatDateByMST(account.account_balance_date)}`}
          </p>
        </Card>
        <Card title={t('dashboard.chartHeader.altv')}>
          <p>${formatCurrency(Number(account?.altv ?? 0), 2)}</p>
        </Card>
        <Card title={t('dashboard.chartHeader.status')}>
          <p
            className={
              !account?.status?.includes('closed')
                ? 'text-[#14CA74]'
                : 'text-primary'
            }
          >
            {account?.status && !account?.status?.includes('closed')
              ? 'Active'
              : 'Disabled'}
          </p>
        </Card>
      </div>
      <Divider />
      <div className={'m-5 w-[50%] text-xs item'}>
        <div className={'flex mb-3 mx-2 justify-between'}>
          <div className={'flex gap-x-1 items-center'}>
            <p>{t('dashboard.chartHeader.activeTradingDays')}</p>
            <ControlUnit value={account?.trading_days?.toString() || '0'} />
          </div>
          <div className={'flex gap-x-1 items-center'}>
            <p>{t('dashboard.chartHeader.activeTradingDays')}</p>
            <ControlUnit
              active
              minWidth={55}
              value={
                account?.status
                  ? account.status.charAt(0).toUpperCase() +
                    account.status.slice(1)
                  : 'N/A'
              }
            />
          </div>
          <div className={'flex gap-x-1'}>
            <p>{t('dashboard.chartHeader.balance')}</p>
            <p className={'font-bold'}>
              ${formatCurrency(Number(account?.cash_balance ?? 0), 2)}
            </p>
          </div>
          <p>
            {account?.account_balance_date
              ? formatDateByMST(account.account_balance_date)
              : 'N/A'}
          </p>
        </div>
        <Divider />
      </div>
    </div>
  );
};
